/**
 * 플래시 세일 관련 API 서비스
 *
 * ✅ curl verified 2026-04-18
 * - /flash-sale/{slug}: returnedStocks에 Product/optionInfo 중첩 (ReturnedStock)
 * - /flash-sale/{id}/returned-stocks: flat 필드 productName/imagePath (ReturnedStockFlat)
 */

import type { ApiClient } from "@/shared/api/client";

// ── 플래시 세일 그룹 (GET /flash-sale 목록) ──

export interface FlashSaleGroup {
  id: number;
  brand_id: number;
  discount_rate: string;
  start_date: string;
  end_date: string;
  status: string;
  sold_at: string | null;
  slug: string;
  createdAt: string;
  updatedAt: string;
  brandName: string;
}

// ── 반품 재입고 (GET /flash-sale/{id}/returned-stocks) — flat shape ──

export interface ReturnedStockFlat {
  id: number;
  product_id: number;
  brand_id: number;
  option_detail_id: number | null;
  has_option: boolean;
  amount: number;
  price: number;
  flash_sale_group_id: number;
  sold_at: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  imagePath: string;
  brandName: string;
  productName: string;
}

// ── 플래시 세일 상세 (GET /flash-sale/{slug}) — nested shape ──

export interface ReturnedStock {
  id: number;
  product_id: number;
  option_detail_id: number | null;
  amount: number;
  price: number;
  Product: {
    id: number;
    name: string;
    imagePath: string;
  };
  optionInfo?: {
    name: string;
    price: number;
  };
}

export interface FlashSaleDetail extends FlashSaleGroup {
  returnedStocks: ReturnedStock[];
}

// ── 슈퍼관리자 일괄 등록 (POST /flash-sale/admin/register) ──

export interface FlashSaleRegisterCreatedItem {
  product_id: number;
  flash_sale_group_id: number;
  slug: string;
}

export interface FlashSaleRegisterSkippedItem {
  product_id: number;
  reason: string;
  flash_sale_group_id?: number;
}

export interface FlashSaleRegisterInvalidItem {
  product_id: number;
  reason: string;
}

export interface FlashSaleRegisterResult {
  requested: number;
  created: FlashSaleRegisterCreatedItem[];
  skipped: FlashSaleRegisterSkippedItem[];
  invalid: FlashSaleRegisterInvalidItem[];
  groupIdRange: { start: number; end: number } | null;
}

// ── 통째 구매 (GET /flash-sale/bulk/quote, POST /flash-sale/bulk/order) ──

export interface FlashSaleBulkStock {
  id: number;
  flashSaleGroupId: number;
  brandId: number;
  productId: number;
  optionDetailId: number | null;
  productName: string;
  imagePath: string;
  amount: number;
  price: number;
  discountRate: number;
  unitPrice: number;
  linePrice: number;
}

export interface FlashSaleBulkQuote {
  setCount: number;
  groupIds: number[];
  returnedStockIds: number[];
  stocks: FlashSaleBulkStock[];
  extraRate: number;
  originTotal: number;
  singleGoodsTotal: number;
  bulkGoodsTotal: number;
  extraDiscount: number;
  shippingFee: number;
  singleShippingTotal: number;
  basicShippingFee: number;
  /** 제주·도서산간 — 세트마다 상자가 따로 나가 세트당 추가 운임을 받는다 */
  isRemoteArea: boolean;
  remoteSurchargePerSet: number;
  remoteSurcharge: number;
  /** 배송지와 무관한 안내용 — 제주·도서산간 세트당 추가 운임 */
  remoteAreaSurchargePerSet: number;
  singleTotal: number;
  totalPrice: number;
  savings: number;
}

export interface FlashSaleBulkOrderRequest {
  returnedStockIds: number[];
  device: "pc" | "mobile";
  shippingInfo: {
    orderName: string;
    orderMobileNo: string;
    orderZipCode: string;
    orderAddress: string;
    orderDetailAddress: string;
    orderMemo: string;
  };
}

export const FLASH_SALE_BULK_CHANGED = "FLASH_SALE_BULK_CHANGED";

// ── 서비스 팩토리 ──

export function createFlashSaleService(client: ApiClient) {
  return {
    /** 플래시 세일 목록 조회 GET /flash-sale */
    getFlashSales: () => client.get<FlashSaleGroup[]>("/flash-sale"),

    /** 플래시 세일 상세 조회 GET /flash-sale/{slug} */
    getFlashSaleDetail: (slug: string) => client.get<FlashSaleDetail>(`/flash-sale/${slug}`),

    /** 반품 재입고 조회 GET /flash-sale/{id}/returned-stocks */
    getReturnedStocks: (id: number) =>
      client.get<ReturnedStockFlat[]>(`/flash-sale/${id}/returned-stocks`),

    /** 통째 구매 견적 — 결제 금액은 서버 계산만 사용 */
    getBulkQuote: (zipCode?: string) =>
      client.get<FlashSaleBulkQuote>("/flash-sale/bulk/quote", {
        params: zipCode ? { zipCode } : undefined,
      }),

    /** 통째 구매 주문 생성 → orderNo */
    createBulkOrder: (body: FlashSaleBulkOrderRequest) =>
      client.post<{ orderNo: string; orderId: number; totalPrice: number }>(
        "/flash-sale/bulk/order",
        body,
      ),

    /** 슈퍼관리자 전용 — 상품번호 목록으로 반짝할인 일괄 등록 */
    registerAdmin: (productIds: number[]) =>
      client.post<FlashSaleRegisterResult>("/flash-sale/admin/register", { productIds }),
  };
}
